<script setup lang="ts">
import {
	computed,
	onMounted,
	ref,
	watch
} from "vue";
import {useRoute, useRouter} from "vue-router";
import {jobsStore, JobsContextType} from "@/store/jobs";
import {searchStore} from "@/store/search";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppSelect from "@/components/ui-kit/select/AppSelect.vue";
import {workerStore} from "@/store/worker";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import CreateEditJobDropdown from "@/components/main/jobs-panel/dropdowns/CreateEditJobDropdown.vue";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import {useJobWorkerOptions} from "@/composables/useJobWorkerOptions";
import {TagEntityType} from "@/models/etc/types";
import type {TagWithFrequency} from "@/models/interfaces/tags/tag-models";
import {clientPageStore} from "@/store/client-page";
import {projectPageStore} from "@/store/project-page";
import { i18n } from "@/i18n";

const t = (key: string): string => String(i18n.global.t(key))

const store = jobsStore()
const userStore = workerStore()
const clientStore = clientPageStore()
const projectStore = projectPageStore()
const route = useRoute()
const router = useRouter()
const searchStoreInstance = searchStore()

const currentContext = computed<{ type: JobsContextType; entityId: number | null }>(() => {
	const id = route.query.id != null && route.query.id !== ''
		? Number(route.query.id)
		: null

	if (route.query.page === 'client' && id) {
		return {
			type: 'client', entityId: id
		}
	}

	if (route.query.page === 'project' && id) {
		return {
			type: 'project', entityId: id
		}
	}

	return { type: 'main', entityId: null }
})

const isOnEntity = computed(() => currentContext.value.type !== 'main')
const isOnClient = computed(() => currentContext.value.type === 'client')
const canCreateJob = computed(() => {
	if (userStore.workerProfile?.IsAdmin) return true
	if (currentContext.value.type === 'client') {
		return clientStore.client?.JobAccess === 2
	}
	if (currentContext.value.type === 'project') {
		return projectStore.project?.JobAccess === 2
	}
	return true
})

const routeJobId = computed<number | null>(() => {
	const raw = route.query.jobId
	if (raw == null || Array.isArray(raw) || raw === '') {
		return null
	}
	const id = Number(raw)
	return Number.isFinite(id) && id > 0 ? id : null
})
const clientSearch = computed(() => store.context.clientSearch)
const holdingSearch = computed(() => store.context.holdingSearch)

const jobsSearch = ref('')
const selectedPerson = ref<number | null>(null)
const listGetCount = 30
const selectedChips = ref<number[]>([])

const chips = computed(() => [
	{
		id: 1,
		name: t('job_in_progress'),
		color: '#86CED2'
	},
	{
		id: 2,
		name: t('job_state_urgent_short'),
		color: '#EE966A'
	},
])

const $createEditJobModal = ref<typeof AppResizableDropdown | null>(null)
const $tagsDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const selectedTags = ref<TagWithFrequency[]>([])
const tagsDirty = ref(false)
const counters = computed(() => store.jobsCounter)
const userForUpdateJobsList = computed(() => store.userForUpdateJobsList)
const currentUser = computed(() => userStore.workerProfile)
const searchInWork = computed(() => selectedChips.value.includes(1))
const searchIsLost = computed(() => selectedChips.value.includes(2))

// Списки исполнителей и закреплённые сверху строки — общий источник на оба селекта.
const {
	workersOptions,
	pinnedCurrentUser,
	personFilterOptions,
	personFilterPinned
} = useJobWorkerOptions()

const jobsInit = () => {
	if (currentUser.value) {
		store.getJobWorkers()
	}
}

const onSearch = () => {
	const tagIds = selectedTags.value.length ? selectedTags.value.map(t => t.Id) : null
	store.getJobs(
		jobsSearch.value,
		selectedPerson.value as number,
		tagIds,
		searchInWork.value,
		searchIsLost.value,
		listGetCount,
		0
	)
	store.getJobsCount(
		jobsSearch.value,
		selectedPerson.value as number,
		tagIds
	)
}

const onTagsUpdate = (newTags: TagWithFrequency[]) => {
	selectedTags.value = newTags
	tagsDirty.value = true
}

const onTagsDropdownClose = () => {
	if (tagsDirty.value) {
		tagsDirty.value = false
		onSearch()
	}
}

const onDropTag = (tag: TagWithFrequency) => {
	const wasLast = selectedTags.value.length === 1
	selectedTags.value = selectedTags.value.filter(t => t.Id !== tag.Id)
	if (wasLast) onSearch()
}

const loadAroundJob = async (jobId: number) => {
	const ctx = currentContext.value

	jobsSearch.value = ''
	selectedTags.value = []
	selectedChips.value = []
	tagsDirty.value = false

	store.setContext(ctx.type, ctx.entityId)
	await store.getJobsAroundOne(jobId)
	await store.getJobsCount()
}

const consumeJobNavigationRequest = async () => {
	const req = searchStoreInstance.pendingJobNavigation
	const ctx = currentContext.value
	if (!req || req.type !== ctx.type || req.entityId !== ctx.entityId) return
	searchStoreInstance.clearPendingJobNavigation()

	await loadAroundJob(req.jobId)
}

// Внешний запрос на перезагрузку списка (например, после смены этапа проекта).
const consumeJobsRefreshRequest = () => {
	const req = store.jobsRefreshRequest
	if (!req) {
		return
	}

	const ctx = currentContext.value
	if (req.type !== ctx.type || req.entityId !== ctx.entityId) {
		return
	}

	store.clearJobsRefreshRequest()
	onSearch()
}

const consumeRouteJobId = async (jobId: number) => {
	await loadAroundJob(jobId)

	const { jobId: _omit, ...rest } = route.query
	router.replace({ query: rest })
}

const applyContextAndReload = () => {
	store.clearJobsRefreshRequest()

	const req = searchStoreInstance.pendingJobNavigation
	if (req && req.type === currentContext.value.type && req.entityId === currentContext.value.entityId) {
		consumeJobNavigationRequest()
		return
	}

	store.setContext(currentContext.value.type, currentContext.value.entityId)
	if (!currentUser.value) return

	if (isOnEntity.value && routeJobId.value) {
		consumeRouteJobId(routeJobId.value)
		return
	}

	const targetUserId = 0
	if (selectedPerson.value !== targetUserId) {
		selectedPerson.value = targetUserId
	} else {
		onSearch()
	}
}

onMounted(() => {
	jobsInit()
	applyContextAndReload()
})

watch(
	() => searchStoreInstance.pendingJobNavigation,
	() => consumeJobNavigationRequest()
)

watch(
	() => store.jobsRefreshRequest,
	() => consumeJobsRefreshRequest()
)

watch(currentUser, (newValue, oldValue) => {
	if (newValue && !oldValue) {
		jobsInit()
		applyContextAndReload()
	}
})

watch(currentContext, (newValue, oldValue) => {
	if (newValue.type !== oldValue?.type || newValue.entityId !== oldValue?.entityId) {
		selectedTags.value = []
		tagsDirty.value = false
		applyContextAndReload()
	}
})

watch(selectedPerson, (newValue, oldValue) => {
	if (newValue !== oldValue && newValue != null) {
		onSearch()
	}
})

watch(userForUpdateJobsList, (newValue, oldValue) => {
	if (newValue && !oldValue) {
		selectedPerson.value = newValue
		jobsSearch.value = ''
		onSearch()
		store.setUserForUpdateJobsList(null)
	}
})

const toggleClientSearch = () => {
	store.setClientSearch(!clientSearch.value)
	onSearch()
}

const toggleHoldingSearch = () => {
	store.setHoldingSearch(!holdingSearch.value)
	onSearch()
}

const toggleChip = (id: number) => {
	if (selectedChips.value.includes(id)) {
		selectedChips.value.splice(selectedChips.value.indexOf(id), 1)
	} else {
		selectedChips.value.push(id)
	}

	onSearch()
}

const chipStyle = (t: any) => {
	const active = selectedChips.value.includes(t.id);
	const base = {
		borderLeftColor: t.color,
		backgroundColor: 'transparent'
	}
	if (active) {
		base.backgroundColor = hexToRgba(t.color, 0.121);
	}

	return base;
}

const chipColoredStyle = (t: any) => {
	return {
		backgroundColor: t.color
	}
}

const hexToRgba = (hex: string, alpha: number) => {
	const res = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	if (!res) return hex;
	const r = parseInt(res[1], 16);
	const g = parseInt(res[2], 16);
	const b = parseInt(res[3], 16);
	return `rgba(${r},${g},${b},${alpha})`;
}
</script>

<template>
	<div class="jobs-panel-search">
		<div class="jobs-panel-search__header">
			<app-resizable-dropdown
				ref="$tagsDropdown"
				position="left"
				:close-by-click-outside="true"
				:max-width="640"
				:max-height="436"
				:mobile-title="$t('choose_tags')"
				name="$jobsPanelTags"
				@close="onTagsDropdownClose"
			>
				<template #activator>
					<app-tooltip position="bottom" :text="$t('ui_tag_filter')">
						<template #activator>
							<app-icon
								class="jobs-panel-search__add-btn"
								name="hash"
								width="20px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
							/>
						</template>
					</app-tooltip>
				</template>
				<app-tag-selector
					:model-value="selectedTags"
					:entity-type="TagEntityType.Job"
					mode="filter"
					@update:model-value="onTagsUpdate"
					@close="$tagsDropdown?.close()"
				/>
			</app-resizable-dropdown>
			<div class="jobs-panel-search__header-inputs">
				<div class="jobs-panel-search__header-inputs-item">
					<app-select
						v-model="selectedPerson"
						:placeholder="$t('select_user_placeholder')"
						:items="personFilterOptions"
						:pinned-items="personFilterPinned"
						:min-dropdown-width="272"
						description-key="status"
						searchable
					/>
				</div>
				<div class="jobs-panel-search__header-inputs-item">
					<app-input
						v-model="jobsSearch"
						:placeholder="$t('search_short')"
						show-search-button
						@press-enter="onSearch"
						@search="onSearch"
						@clear-field="onSearch"
					/>
				</div>
			</div>
			<app-resizable-dropdown
				v-if="canCreateJob"
				position="right"
				resizable
				:initial-width="320"
				:initial-height="398"
				:min-width="320"
				:max-width="480"
				:min-height="398"
				:max-height="600"
				:close-by-click-outside="false"
				:hide-by-parent-scroll="false"
				:mobile-title="$t('job_create_title')"
				ref="$createEditJobModal"
				name="$createEditJobModal"
			>
				<template #activator>
					<app-tooltip position="top" :text="$t('job_create')">
						<template #activator>
							<app-icon
								class="jobs-panel-search__add-btn"
								name="plus-bold"
								width="22px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
							/>
						</template>
					</app-tooltip>
				</template>
				<create-edit-job-dropdown
					type="create"
					:workers="workersOptions"
					:pinned-workers="pinnedCurrentUser"
					@on-close="$createEditJobModal?.close()"
				/>
			</app-resizable-dropdown>
			<app-icon
				v-else
				class="jobs-panel-search__add-btn"
				name="plus-bold"
				width="22px"
				color="#D2D2D2"
			/>
		</div>
		<div class="jobs-panel-search__row jobs-panel-search__row--checkboxes">
			<div
				class="jobs-panel-search__chip"
				:style="chipStyle(chips[0])"
				@click="toggleChip(chips[0].id)"
			>
				<div
					class="jobs-panel-search__chip-colored"
					:style="chipColoredStyle(chips[0])"
				/>
				<div class="jobs-panel-search__chip-count">{{ `${chips[0].name}: ${counters?.Actual || 0}` }}</div>
			</div>
			<div
				class="jobs-panel-search__chip"
				:style="chipStyle(chips[1])"
				@click="toggleChip(chips[1].id)"
			>
				<div
					class="jobs-panel-search__chip-colored"
					:style="chipColoredStyle(chips[1])"
				/>
				<div class="jobs-panel-search__chip-count">{{ `${chips[1].name}: ${counters?.Lost || 0}` }}</div>
			</div>
		</div>
		<div
			v-if="isOnClient"
			class="jobs-panel-search__row jobs-panel-search__row--toggle"
		>
			<app-tooltip
				class="jobs-panel-search__toggle-tooltip"
				position="top"
				:text="$t(clientSearch ? 'job_filter_show_all' : 'job_filter_client_only')"
			>
				<template #activator>
					<div
						:class="{
							'jobs-panel-search__toggle-btn': true,
							'jobs-panel-search__toggle-btn--active': clientSearch,
						}"
						@click="toggleClientSearch()"
					>
						<app-icon
							class="jobs-panel-search__toggle-btn-icon"
							name="filter"
							width="11px"
							:color="clientSearch ? '#FFF' : '#979797'"
							cursor="pointer"
						/>
						{{ $t('job_filter_client_only_label') }}
					</div>
				</template>
			</app-tooltip>
			<app-tooltip
				class="jobs-panel-search__toggle-tooltip"
				position="top"
				:text="$t(holdingSearch ? 'job_filter_current_client' : 'job_filter_holding_clients')"
			>
				<template #activator>
					<div
						:class="{
							'jobs-panel-search__toggle-btn': true,
							'jobs-panel-search__toggle-btn--active': holdingSearch,
						}"
						@click="toggleHoldingSearch()"
					>
						<app-icon
							class="jobs-panel-search__toggle-btn-icon"
							name="filter"
							width="11px"
							:color="holdingSearch ? '#FFF' : '#979797'"
							cursor="pointer"
						/>
						{{ $t('job_filter_with_holding_label') }}
					</div>
				</template>
			</app-tooltip>
		</div>
		<div
			v-if="selectedTags.length"
			class="jobs-panel-search__tags"
		>
			<div
				v-for="tag in selectedTags"
				:key="tag.Id"
				class="jobs-panel-search__tags-item"
			>
				<div class="jobs-panel-search__tags-item-name">
					{{ tag.Name }}
				</div>
				<app-icon
					class="jobs-panel-search__tags-item-delete"
					name="close-bold"
					width="12px"
					color="#D2D2D2"
					hover-color="#4D4D4D"
					cursor="pointer"
					@click.stop="onDropTag(tag)"
				/>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
.jobs-panel-search {
	display: flex;
	flex-direction: column;
	width: 100%;
	padding: 8px 8px 0;

	&__header {
		display: flex;
		align-items: center;
		gap: 4px;

		&-inputs {
			flex: 1;
			min-width: 0;
			display: flex;
			align-items: center;
			gap: 4px;

			&-item {
				width: 50%;
				max-width: 50%;
				min-width: 0;
			}
		}
	}

	&__chip {
		flex: 1;
		display: inline-flex;
		align-items: stretch;
		position: relative;
		background: #fff;
		border-radius: 6px;
		color: #979797;
		cursor: pointer;
		user-select: none;
		box-shadow: 0 0 1px 0.5px #D2D2D2 inset;
		overflow: hidden;

		&-colored {
			width: 4px;
			flex-shrink: 0;
		}

		&-count {
			display: flex;
			justify-content: center;
			font-size: 14px;
			line-height: 22px;
			font-weight: 400;
			min-width: 24px;
			height: 26px;
			color: #4D4D4D;
			flex-grow: 1;
			padding: 2px 5px 2px 3px;
		}
	}

	&__row {
		display: flex;
		align-items: center;
		color: #333;
		position: relative;

		&--inputs {
			display: flex;
			gap: 4px;
			width: 100%;
			margin-bottom: 0;
		}

		&--checkboxes {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 4px;
			margin-top: 8px;
			margin-left: -8px;
			margin-right: -8px;
			padding: 0 8px 8px;
			border-bottom: 1px solid #F2F2F2;

			&:has(+ .jobs-panel-search__row--toggle) {
				border-bottom: none;
				padding-bottom: 0;
			}
		}

		&--toggle {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 4px;
			margin-top: 8px;
			margin-left: -8px;
			margin-right: -8px;
			padding: 0 8px 8px;
			border-bottom: 1px solid #F2F2F2;
		}
	}

	&__toggle-tooltip {
		flex: 1;
		min-width: 0;

		&.app-tooltip .app-tooltip__activator {
			display: block;
		}
	}

	&__toggle-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 2px;
		padding: 2px 4px;
		font-size: 12px;
		line-height: 16px;
		font-weight: 500;
		color: #979797;
		background: #FFF;
		border-radius: 6px;
		box-shadow: 0 0 1px 0.5px #D2D2D2 inset;
		cursor: pointer;
		user-select: none;

		&:hover {
			background: #F3F8F4;
		}

		&--active {
			color: #FFF;
			background: #6DA279;

			&:hover {
				background: #6DA27980;
			}
		}

		&-icon {
			margin-top: -1px;
		}
	}

	&__sort-btn {
		position: absolute;
		left: 0;
	}

	&__search {
		width: 100%;
	}

	&__tags {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 4px;
		width: 100%;
		margin-top: 8px;
		padding: 0 0 8px;

		&-item {
			display: flex;
			align-items: center;
			text-transform: lowercase;

			&-name {
				color: #979797;
				font-size: 14px;
				line-height: 18px;
				margin-right: 2px;
			}
		}
	}

}
.dropdown-divider {
	height: 0.5px;
	background: #000;
}
</style>
